/**
 * ReplayPane - un volet de replay : en-tête optionnel + table.
 *
 * Utilisé une fois par ReplayMode (vue simple) et deux fois par ReplayDualMode
 * (vue double tables synchronisées).
 */

import { useMemo } from 'react';
import ReplayTable from './ReplayTable';
import type { GameHistory } from '../game/gameHistory';
import { computeReplayStepState } from '../game/gameHistory';
import { stepCursorKind } from '../game/replayCursor';

interface ReplayPaneProps {
  history: GameHistory;
  stepIndex: number;
  subStep: number;
  header?: React.ReactNode;
  className?: string;
  /** Masque la table (anti-spoiler) derrière un bouton de révélation. */
  masked?: boolean;
  maskMessage?: string;
  maskActionLabel?: string;
  onReveal?: () => void;
}

export default function ReplayPane({
  history,
  stepIndex,
  subStep,
  header,
  className,
  masked,
  maskMessage,
  maskActionLabel,
  onReveal,
}: ReplayPaneProps) {
  const currentStep = history.steps[stepIndex] || history.steps[0];

  const stepState = useMemo(
    () => computeReplayStepState(history, currentStep, subStep),
    [history, currentStep, subStep],
  );

  if (!currentStep) return null;

  return (
    <div className={`replay-pane ${className ?? ''}`}>
      {header}
      <div className="replay-pane-body">
        <ReplayTable
          history={history}
          stepState={stepState}
          showBids={stepCursorKind(currentStep) === 'bids'}
          showChien={currentStep.type === 'chien'}
          showEcart={currentStep.type === 'ecart'}
        />
        {masked && (
          <div className="replay-pane-mask">
            <div className="replay-pane-mask-content">
              <p className="replay-pane-mask-text">
                {maskMessage ?? 'Table masquée pour ne pas dévoiler les mains adverses.'}
              </p>
              {onReveal && (
                <button type="button" className="replay-pane-mask-btn" onClick={onReveal}>
                  {maskActionLabel ?? 'Révéler'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
